import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GST_RATES,
  PRODUCT_CATEGORIES,
  PRODUCT_MEDIA_ENTITY_TYPE,
  STOCK_UNITS,
  canonicalUnitOf,
  isStockableCategory,
  paiseToRupees,
  parseRupees,
  type GstRate,
  type Product,
  type ProductCategory,
  type StockUnit,
} from '@tailoring-crm/shared';
import { useHasModule } from '../../lib/billing/entitlements.js';
import { useStockList } from '../../lib/inventory/use-stock.js';
import { useCreateProduct, useUpdateProduct } from '../../lib/products/use-products.js';
import { MediaUploader } from '../media/media-uploader.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * Adding a line to the price list, or correcting one.
 *
 * THE PRICE MAY BE LEFT BLANK, and the hint says so rather than leaving somebody to discover it.
 * "Bridal blouse — price on the piece" is a real catalogue entry, and the alternative a form
 * without this affordance produces is a shop typing 0, which then reads as free on every screen
 * that totals a list.
 *
 * THE FORM SAYS OUT LOUD THAT EDITING IS SAFE. "Blouse" going up to ₹950 is a change to what the
 * counter will quote NEXT — every quotation already sent keeps its own figure — and a shop that
 * did not know that would leave stale prices in the catalogue rather than risk rewriting history.
 */

export interface ProductFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present for an edit; absent to add a new line. */
  product?: Product | null;
  onSaved?: () => void;
}

/** `paiseToRupees` for a text input: no symbol, no grouping, no trailing `.00`. */
function rupeeInputValue(paise: number | null): string {
  if (paise === null || paise <= 0) return '';
  const rupees = paiseToRupees(paise);
  return Number.isInteger(rupees) ? String(rupees) : rupees.toFixed(2);
}

export function ProductFormDialog({
  open,
  onOpenChange,
  product = null,
  onSaved,
}: ProductFormDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fieldId = useId();

  const isEdit = product !== null;
  const create = useCreateProduct();
  const update = useUpdateProduct();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [category, setCategory] = useState<ProductCategory>('stitching');
  const [price, setPrice] = useState('');
  const [unit, setUnit] = useState<StockUnit>('piece');
  /** `''` takes the shop's default rate on the bill. */
  const [gstRate, setGstRate] = useState<string>('');
  const [hsnCode, setHsnCode] = useState('');
  /** `itemId:variantId`, or `''` for a product that is not tracked. */
  const [stockLink, setStockLink] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [imageMediaId, setImageMediaId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(product?.name ?? '');
    setCode(product?.code ?? '');
    setCategory(product?.category ?? 'stitching');
    setPrice(rupeeInputValue(product?.unitPricePaise ?? null));
    setUnit(product?.unit ?? 'piece');
    setGstRate(product?.gstRatePercent == null ? '' : String(product.gstRatePercent));
    setHsnCode(product?.hsnCode ?? '');
    setStockLink(
      product?.stockItemId && product.stockVariantId
        ? `${product.stockItemId}:${product.stockVariantId}`
        : '',
    );
    setDescription(product?.description ?? '');
    setActive(product?.active ?? true);
    setImageMediaId(product?.imageMediaId ?? null);
    setFormError(null);
  }, [open, product]);

  /**
   * The shelf a counter sale of this product draws down. Offered only to a shop that keeps stock,
   * only on goods, and only from stock counted the same way the product is priced — a product
   * sold per piece cannot draw on a roll counted in metres.
   */
  const tracksStock = useHasModule('inventory');
  const offersStockLink = tracksStock && isStockableCategory(category);
  const stock = useStockList({}, { enabled: open && offersStockLink });
  const stockChoices = (stock.data?.items ?? [])
    .filter((item) => canonicalUnitOf(item.unit) === canonicalUnitOf(unit))
    .flatMap((item) =>
      item.variants
        .filter((variant) => !variant.archived)
        .map((variant) => ({
          value: `${item.id}:${variant.id}`,
          label: `${item.name} — ${variant.colour}`,
        })),
    );

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void (async () => {
      setFormError(null);

      const trimmedName = name.trim();
      if (trimmedName.length === 0) {
        setFormError(t('products.nameRequired'));
        return;
      }

      /**
       * Blank means "not priced", which is a different fact from zero — so it is sent as null
       * rather than as 0. Anything typed that is not a number is refused here rather than
       * silently counted as nothing: a mistyped price is the one field on this form nobody would
       * notice going wrong.
       */
      let unitPricePaise: number | null = null;
      if (price.trim() !== '') {
        try {
          unitPricePaise = parseRupees(price);
        } catch {
          setFormError(t('products.priceInvalid'));
          return;
        }
      }

      /**
       * A shop without the inventory module never sees the picker, so it never sends the link
       * either — an edit there must not quietly clear a link made while it had the module. A goods
       * line sends whatever is picked; any other category sends none, which the server requires.
       */
      const [stockItemId, stockVariantId] =
        offersStockLink && stockLink ? stockLink.split(':') : [null, null];
      const stockFields = tracksStock
        ? { stockItemId: stockItemId ?? null, stockVariantId: stockVariantId ?? null }
        : {};
      const input = {
        name: trimmedName,
        code: code.trim() === '' ? null : code.trim(),
        category,
        unitPricePaise,
        unit,
        gstRatePercent: gstRate === '' ? null : (Number(gstRate) as GstRate),
        hsnCode: hsnCode.trim() === '' ? null : hsnCode.trim(),
        ...stockFields,
        description: description.trim() === '' ? null : description.trim(),
        imageMediaId,
        active,
      };

      try {
        if (isEdit) {
          await update.mutateAsync({ productId: product.id, input });
        } else {
          await create.mutateAsync(input);
        }
        toast({
          title: isEdit ? t('products.saved') : t('products.created', { name: trimmedName }),
          variant: 'success',
        });
        onSaved?.();
        onOpenChange(false);
      } catch (error) {
        setFormError(error instanceof Error ? error.message : t('error.unexpected'));
      }
    })();
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('products.editTitle') : t('products.addTitle')}</DialogTitle>
          <DialogDescription>{t('products.formDescription')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-name`}>{t('products.nameLabel')}</Label>
            <Input
              id={`${fieldId}-name`}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-category`}>{t('products.categoryLabel')}</Label>
            <select
              id={`${fieldId}-category`}
              value={category}
              onChange={(event) => setCategory(event.target.value as ProductCategory)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {PRODUCT_CATEGORIES.map((one) => (
                <option key={one} value={one}>
                  {t(`products.category.${one}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-price`}>{t('products.priceLabel')}</Label>
            <Input
              id={`${fieldId}-price`}
              inputMode="decimal"
              value={price}
              aria-describedby={`${fieldId}-price-hint`}
              onChange={(event) => setPrice(event.target.value)}
            />
            <p id={`${fieldId}-price-hint`} className="text-sm text-muted-foreground">
              {t('products.priceHint')}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-unit`}>{t('products.unitLabel')}</Label>
              <select
                id={`${fieldId}-unit`}
                value={unit}
                onChange={(event) => setUnit(event.target.value as StockUnit)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                {STOCK_UNITS.map((one) => (
                  <option key={one} value={one}>
                    {t(`stock.unit.${one}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-gst`}>{t('products.gstLabel')}</Label>
              <select
                id={`${fieldId}-gst`}
                value={gstRate}
                onChange={(event) => setGstRate(event.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">{t('products.gstDefault')}</option>
                {GST_RATES.map((rate) => (
                  <option key={rate} value={String(rate)}>
                    {rate}%
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-hsn`}>{t('products.hsnLabel')}</Label>
              <Input
                id={`${fieldId}-hsn`}
                inputMode="numeric"
                value={hsnCode}
                onChange={(event) => setHsnCode(event.target.value)}
              />
            </div>
          </div>
          <p className="-mt-2 text-sm text-muted-foreground">{t('products.taxHint')}</p>

          {offersStockLink ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-stock`}>{t('products.stockLabel')}</Label>
              <select
                id={`${fieldId}-stock`}
                value={stockLink}
                aria-describedby={`${fieldId}-stock-hint`}
                onChange={(event) => setStockLink(event.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">{t('products.stockNone')}</option>
                {stockChoices.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </select>
              <p id={`${fieldId}-stock-hint`} className="text-sm text-muted-foreground">
                {t('products.stockHint')}
              </p>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-code`}>{t('products.codeLabel')}</Label>
            <Input
              id={`${fieldId}-code`}
              value={code}
              aria-describedby={`${fieldId}-code-hint`}
              onChange={(event) => setCode(event.target.value)}
            />
            <p id={`${fieldId}-code-hint`} className="text-sm text-muted-foreground">
              {t('products.codeHint')}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-description`}>{t('products.descriptionLabel')}</Label>
            <textarea
              id={`${fieldId}-description`}
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">{t('products.imageLabel')}</span>
            <MediaUploader
              entityType={PRODUCT_MEDIA_ENTITY_TYPE}
              entityId={product?.id ?? null}
              maxFiles={1}
              onChange={(media) => setImageMediaId(media[0]?.id ?? null)}
            />
          </div>

          <div className="flex items-start gap-3 text-sm">
            <input
              id={`${fieldId}-active`}
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 accent-primary"
              checked={active}
              onChange={(event) => setActive(event.target.checked)}
            />
            <div className="flex flex-col gap-0.5">
              <label htmlFor={`${fieldId}-active`} className="font-medium">
                {t('products.activeLabel')}
              </label>
              <p className="text-muted-foreground">{t('products.activeHint')}</p>
            </div>
          </div>

          {formError ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {formError}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {isEdit ? t('common.save') : t('products.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
